import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Checkbox, InputNumber, Modal, Select, Switch, notification } from 'antd';
import { MinusCircle, PlusCircle } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import designServices, { type RetrieveSystemDesignResponse } from '../../../../services/oneButtonServices/designServices';
import packingSlipService from '../../../../services/packingSlipService';
import { enableArrayOverride, solarFinancePartnerId } from './enableManualMode';

const FASTENER_TYPES = ['Splice', 'Mid-Clamp', 'End-Clamp', 'MLPE Bracket'];
const MAX_INVERTERS = 4;
/** Same key order V1 sends to the domestic-content calculator. */
const DOMESTIC_PART_ORDER = [
    'Module', 'Optimizer', 'Racking System', 'Rail', 'Splice', 'Mid-Clamp', 'End-Clamp',
    'MLPE Bracket', 'Inverter', 'Inverter2', 'Inverter3', 'Inverter4',
];

const EQUIPMENT_WIDTH_COLUMNS = ['type', 'model', 'manufacturer', 'quantity', 'arrays'];
const PRODUCTION_WIDTH_COLUMNS = ['array', 'tilt', 'azimuth', 'solar', 'building', 'modules'];

/** Columns with a percent keep that percent. Columns that are not set share the remaining width equally. */
function applySharedColumnWidths(el: HTMLElement, columns: string[], sourcePrefix: string, targetPrefix: string) {
    const style = getComputedStyle(el);
    const specified = columns.map((column) => {
        const raw = style.getPropertyValue(`${sourcePrefix}${column}`).trim();
        const value = parseFloat(raw);
        return Number.isFinite(value) && value > 0 ? value : null;
    });
    const used = specified.reduce<number>((sum, value) => sum + (value ?? 0), 0);
    const open = specified.filter((value) => value == null).length;
    const share = open > 0 ? Math.max(0, 100 - used) / open : 0;
    columns.forEach((column, index) => {
        const width = specified[index] == null ? share : specified[index];
        el.style.setProperty(`${targetPrefix}${column}`, `${width}%`);
    });
}

/**
 * Same Excel call as V1 `calculateDomesticPercentage`.
 * Dev oneBUTTON (devops :5020) posts to the POC cascading service and reads cell E4 only.
 * Part key order is required — the workbook maps slots by position.
 */
async function fetchV1DomesticPercent(parts: Record<string, string>, financePartnerId: number): Promise<number> {
    const cascadingUrl = String(import.meta.env.VITE_EXCELSERVICES_API_URL || '').trim();
    const tokenUrl = String(import.meta.env.VITE_EXCELSERVICES_TOKEN_API_URL || '').trim();
    const tokenRes = await axios.get(tokenUrl);
    const ordered: Record<string, string> = {};
    DOMESTIC_PART_ORDER.forEach((key) => { ordered[key] = parts[key] ?? ''; });
    const response = await axios.post(cascadingUrl, {
        ExcelFileName: 'Net Suite Equipment Calculator',
        SheetName: 'DC Evaluation IO V3',
        InputValues: [{
            CellNumber: 'C4',
            Type: 'string',
            CellValue: {
                financePartnerId: String(financePartnerId),
                environment: import.meta.env.VITE_EXCEL_ENV_NAME,
                partCount: String(DOMESTIC_PART_ORDER.length),
                parts: ordered,
            },
        }],
        OutputCells: ['E4'],
    }, { headers: { Authorization: `Bearer ${tokenRes.data?.token}` } });
    const cell = (response.data?.OutputValues || []).find((item: { CellNumber?: string }) => item?.CellNumber === 'E4');
    const val = JSON.parse(cell?.CellValue || '{}')?.evaluationResult?.systemDCPercentage;
    const numeric = Number(val);
    return Number.isFinite(numeric) ? Number((numeric * 100).toFixed(2)) : 0;
}

type OptionItem = {
    id: number | null;
    manufacturer: string | null;
    model: string | null;
    isDomestic?: boolean;
    rackingModelId?: number | null;
    isDefault?: boolean;
};

type EqRow = {
    id: string;
    type: string;
    quantity: number;
    arrays: number[];
    options: OptionItem[];
    selectedModel: number | null;
    selectedManufacturer: string | null;
    moduleText: string | null;
    isRemovable: boolean;
};

type MasterData = Record<string, any> | null;

/** V1 `solar-manual-array-details.validate()`. Blocks save until every array is on an inverter. */
function validateManualRows(rows: EqRow[], arrayNumbers: number[]): string[] {
    const messages: string[] = [];
    const inverterRows = rows.filter((row) => row.type.toLowerCase().includes('inverter'));
    const assigned = new Set<number>();
    inverterRows.forEach((row) => row.arrays.forEach((n) => assigned.add(Number(n))));
    const unassigned = arrayNumbers.filter((n) => !assigned.has(n)).sort((a, b) => a - b);
    if (unassigned.length > 0) {
        const missing = unassigned.length === 1
            ? String(unassigned[0])
            : `${unassigned.slice(0, -1).join(', ')} & ${unassigned[unassigned.length - 1]}`;
        const plural = unassigned.length > 1;
        messages.push(`Array${plural ? 's' : ''} ${missing} ${plural ? 'are' : 'is'} currently unassigned. Please ensure each array is linked to an inverter.`);
    }
    inverterRows.forEach((row, index) => {
        const label = row.type || `Inverter ${index + 1}`;
        if (!row.arrays.length) {
            messages.push(`${label} is not attached to any arrays. Select an array, or remove inverters without arrays.`);
        }
        if (row.selectedModel == null) {
            messages.push(`Inverter model has not been selected for ${label}.`);
        }
    });
    return messages;
}

function canonicalArrayNum(array: Record<string, any>, index: number): number {
    if (array.ArrayNumber != null) {
        return Array.isArray(array.ArrayNumber) ? Number(array.ArrayNumber[0]) : Number(array.ArrayNumber);
    }
    return array.Index != null ? Number(array.Index) : index + 1;
}

/** V1 `isValidArrayNum` — 0 / empty means the inverter was never assigned an array. */
function isValidArrayNum(value: unknown): boolean {
    if (Array.isArray(value)) return value.length > 0 && value.some((item) => Number(item) > 0);
    const num = Number(value);
    return value != null && value !== '' && Number.isFinite(num) && num !== 0;
}

/**
 * V1 `normalizeWorkingArrays`. A single inverter with no array number belongs to that design array.
 * Clones so the stored design is left unchanged until Save.
 */
function preparedArrays(designArrays: Record<string, any>[]): Record<string, any>[] {
    return designArrays.map((array, index) => {
        const inverters = Array.isArray(array.Inverters) ? array.Inverters : (array.Inverters ? [array.Inverters] : []);
        const canon = canonicalArrayNum(array, index);
        return {
            ...array,
            Inverters: inverters.map((inv: any) => (
                inverters.length === 1 && inv && !isValidArrayNum(inv.ArrayNumber)
                    ? { ...inv, ArrayNumber: canon }
                    : inv
            )),
            RackingParts: Array.isArray(array.RackingParts) ? array.RackingParts : [],
            RackingType: Array.isArray(array.RackingType) ? array.RackingType : (array.RackingType ? [array.RackingType] : []),
        };
    });
}

function inverterArrayNums(inv: any, canon: number): number[] {
    const raw = isValidArrayNum(inv?.ArrayNumber) ? inv.ArrayNumber : canon;
    const list = Array.isArray(raw) ? raw : [raw];
    const nums = list.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? [...new Set(nums)] : [canon];
}

function mapOpts(src: any[] | undefined, idKey: string, manKey: string, modelKey: string): OptionItem[] {
    return (src || []).map((item) => ({
        id: item[idKey] == null ? null : Number(item[idKey]),
        manufacturer: item[manKey] ?? null,
        model: item[modelKey] ?? null,
        isDomestic: !!item.Is_Domestic,
        rackingModelId: item.Racking_Model_Id == null ? null : Number(item.Racking_Model_Id),
    }));
}

function buildFastenerMap(master: MasterData): Record<string, OptionItem[]> {
    const manMap = new Map<number, string>();
    (master?.RackingManufacturers || []).forEach((rm: any) => {
        if (rm.Racking_Manufacturer_Id) manMap.set(Number(rm.Racking_Manufacturer_Id), rm.Racking_Manufacturer_Name);
    });
    const map: Record<string, OptionItem[]> = {};
    (master?.RackingParts || []).forEach((rp: any) => {
        const type = String(rp.Racking_SubType || 'Other').trim();
        if (!map[type]) map[type] = [];
        const resolved = rp.Racking_Manufacturer_Id ? manMap.get(Number(rp.Racking_Manufacturer_Id)) : null;
        map[type].push({
            id: rp.Racking_Parts_Id == null ? null : Number(rp.Racking_Parts_Id),
            manufacturer: resolved ?? rp.Racking_Manufacturer_Name ?? null,
            model: rp.Racking_Part_Name ?? null,
            isDomestic: !!rp.Is_Domestic,
            rackingModelId: rp.Racking_Model_Id == null ? null : Number(rp.Racking_Model_Id),
            isDefault: !!rp.Is_Default,
        });
    });
    return map;
}

function resolveOption(options: OptionItem[], modelName: unknown, modelId: unknown): OptionItem | undefined {
    const id = Number(modelId);
    if (Number.isFinite(id) && id > 0) {
        const byId = options.find((o) => Number(o.id) === id);
        if (byId) return byId;
    }
    const name = String(modelName ?? '').trim();
    if (!name) return undefined;
    return options.find((o) => o.model === name);
}

function filterMaster(master: MasterData, domesticOnly: boolean): MasterData {
    if (!master || !domesticOnly) return master;
    const keep = (list: any[] | undefined) => (list || []).filter((item) => item?.Is_Domestic);
    return {
        ...master,
        ModuleModels: keep(master.ModuleModels),
        InverterModels: keep(master.InverterModels),
        MonitorModels: keep(master.MonitorModels),
        OptimizerModels: keep(master.OptimizerModels),
        RackingModels: keep(master.RackingModels),
        RackingParts: keep(master.RackingParts),
    };
}

function normPartType(value: string): string {
    return value.trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/s$/, '');
}

function fastenerSource(fastenerMap: Record<string, OptionItem[]>, type: string): OptionItem[] {
    const wanted = normPartType(type);
    const found = Object.entries(fastenerMap).filter(([key]) => normPartType(key) === wanted);
    if (found.length) return found.flatMap(([, list]) => list);
    const fallbackKey = wanted === 'rail' ? 'Rails' : 'Fasteners';
    return fastenerMap[fallbackKey] || [];
}

function subtypeMatches(subtype: string, rowType: string): boolean {
    return !!subtype && normPartType(subtype) === normPartType(rowType);
}

type SavedPart = { id: number; qty: number; type: string };

function savedRackingParts(designArrays: Record<string, any>[], fastenerMap: Record<string, OptionItem[]>): SavedPart[] {
    const records: SavedPart[] = [];
    const subtypeOf = (id: number) => {
        for (const [key, list] of Object.entries(fastenerMap)) {
            if (list.some((option) => Number(option.id) === id)) return key;
        }
        return '';
    };
    designArrays.forEach((array) => {
        const withQty = Array.isArray(array.RackingPartsWithQuantity) ? array.RackingPartsWithQuantity : [];
        if (withQty.length) {
            withQty.forEach((part: any) => {
                const id = Number(part.RackingPartsId);
                if (!Number.isFinite(id) || id <= 0) return;
                records.push({ id, qty: Number(part.RackingQty) || 1, type: String(part.RackingType || subtypeOf(id)) });
            });
            return;
        }
        const ids = Array.isArray(array.RackingParts) ? array.RackingParts : [];
        const types = Array.isArray(array.RackingType) ? array.RackingType : [];
        ids.forEach((raw: any, index: number) => {
            const id = Number(raw);
            if (!Number.isFinite(id) || id <= 0) return;
            records.push({ id, qty: 1, type: String(types[index] || subtypeOf(id)) });
        });
    });
    return records;
}

/** V1 `updateRackingPartsBySystem` plus the saved `RackingParts` id list. */
function applyRackingParts(rows: EqRow[], fastenerMap: Record<string, OptionItem[]>, rackingModelId: number | null, designArrays: Record<string, any>[]): void {
    const saved = savedRackingParts(designArrays, fastenerMap);
    rows.forEach((row) => {
        const type = row.type?.trim();
        if (!type || type === 'Racking System') return;
        const lower = type.toLowerCase();
        const isPartRow = lower === 'rail' || FASTENER_TYPES.some((name) => lower === name.toLowerCase());
        if (!isPartRow) return;
        const source = fastenerSource(fastenerMap, type);
        if (!source.length) return;
        const filtered = rackingModelId ? source.filter((part) => Number(part.rackingModelId) === Number(rackingModelId)) : source;
        const savedHit = source.find((option) => saved.some((part) => part.id === Number(option.id) && subtypeMatches(part.type, type)))
            || source.find((option) => saved.some((part) => part.id === Number(option.id)));
        const inFiltered = savedHit && filtered.some((option) => Number(option.id) === Number(savedHit.id)) ? savedHit : undefined;
        const chosen = inFiltered || savedHit || filtered.find((option) => option.isDefault) || source.find((option) => option.isDefault);
        const options = filtered.length ? filtered : source;
        row.options = chosen?.id && !options.some((option) => Number(option.id) === Number(chosen.id))
            ? [...options, chosen]
            : options;
        if (!rackingModelId || !chosen?.id) {
            row.selectedModel = null;
            row.selectedManufacturer = null;
            row.quantity = 0;
            return;
        }
        const savedPart = saved.find((part) => part.id === Number(chosen.id));
        row.selectedModel = Number(chosen.id);
        row.selectedManufacturer = chosen.model?.includes('Non-Domestic') ? null : (chosen.manufacturer ?? null);
        row.quantity = savedPart && Number(savedPart.qty) > 0 ? Number(savedPart.qty) : 1;
    });
}

/** V1 `updateRackingPartsBySystem` when the user picks a different racking system. */
function rebindPartsForRackingSystem(
    rows: EqRow[],
    fastenerMap: Record<string, OptionItem[]>,
    rackingModelId: number | null,
    designArrays: Record<string, any>[],
): EqRow[] {
    const saved = savedRackingParts(designArrays, fastenerMap);
    return rows.map((row) => {
        const lower = row.type.toLowerCase();
        const isPartRow = lower === 'rail' || FASTENER_TYPES.some((name) => lower === name.toLowerCase());
        if (!isPartRow) return row;
        const source = fastenerSource(fastenerMap, row.type);
        if (!rackingModelId) {
            return { ...row, options: [], selectedModel: null, selectedManufacturer: null, quantity: 0, moduleText: null };
        }
        const filtered = source.filter((part) => Number(part.rackingModelId) === Number(rackingModelId));
        const matched = filtered.find((option) => saved.some((part) => part.id === Number(option.id) && (!part.type || subtypeMatches(part.type, row.type))))
            || filtered.find((option) => saved.some((part) => part.id === Number(option.id)));
        const chosen = matched || filtered.find((option) => option.isDefault);
        if (!chosen?.id) {
            return { ...row, options: filtered, selectedModel: null, selectedManufacturer: null, quantity: 0, moduleText: null };
        }
        const savedPart = matched ? saved.find((part) => part.id === Number(chosen.id)) : undefined;
        return {
            ...row,
            options: filtered,
            selectedModel: Number(chosen.id),
            selectedManufacturer: chosen.model?.includes('Non-Domestic') ? null : (chosen.manufacturer ?? null),
            moduleText: chosen.model ?? null,
            quantity: savedPart && Number(savedPart.qty) > 0 ? Number(savedPart.qty) : 1,
        };
    });
}

function optionsForType(type: string, inverterOptions: OptionItem[], optimizerOptions: OptionItem[], rackingOptions: OptionItem[], fastenerMap: Record<string, OptionItem[]>): OptionItem[] {
    const lower = type.toLowerCase();
    if (lower.includes('inverter')) return inverterOptions;
    if (lower.includes('optimizer')) return optimizerOptions;
    if (lower.includes('racking system')) return rackingOptions;
    if (lower.includes('rail') || FASTENER_TYPES.some((f) => lower.includes(f.toLowerCase()))) {
        return fastenerMap[type] || fastenerMap[`${type}s`] || fastenerMap[lower.includes('rail') ? 'Rails' : 'Fasteners'] || [];
    }
    return [];
}

function buildRows(sourceArrays: Record<string, any>[], master: MasterData, idRef: { current: number }): EqRow[] {
    const designArrays = preparedArrays(sourceArrays);
    const nextId = () => `local-${idRef.current++}`;
    const filtered = master;
    const inverterOptions = mapOpts(filtered?.InverterModels, 'Inverter_Model_Id', 'Inverter_Manufacturer_Name', 'Inverter_Model_Name');
    const optimizerOptions = mapOpts(filtered?.OptimizerModels, 'Optimizer_Model_Id', 'Optimizer_Manufacturer_Name', 'Optimizer_Model_Name');
    const rackingOptions = mapOpts(filtered?.RackingModels, 'Racking_Model_Id', 'Racking_Manufacturer_Name', 'Racking_Model_Name');
    const fastenerMap = buildFastenerMap(filtered);
    const first = designArrays[0] ?? {};
    const moduleQty = designArrays.reduce((sum, array) => sum + (Number(array.Quantity) || 0), 0);
    const rows: EqRow[] = [];

    const moduleModel = first.ModuleModelName || first.Module_Model_Name || first.ModuleModel || '';
    rows.push({
        id: nextId(),
        type: 'Module',
        quantity: moduleQty,
        arrays: [],
        options: [],
        selectedModel: Number(first.ModuleModelId || first.Module_Model_Id || first.ModuleModel) || null,
        selectedManufacturer: first.ModuleManufactuterName || first.Module_Manufacturer_Name || first.ModuleManufacturer || null,
        moduleText: moduleModel ? String(moduleModel) : null,
        isRemovable: false,
    });

    const groups = new Map<string, EqRow>();
    designArrays.forEach((array, index) => {
        const canon = canonicalArrayNum(array, index);
        const inverters = Array.isArray(array.Inverters) ? array.Inverters : [];
        inverters.forEach((inv: any) => {
            if (!inv || (Number(inv.Quantity || 0) <= 0 && !inv.InverterLabel)) return;
            const label = inv.InverterLabel ? String(inv.InverterLabel).trim() : '';
            const key = label || `arr:${canon}:${index}`;
            const nums = inverterArrayNums(inv, canon);
            const match = resolveOption(inverterOptions, inv.Model, inv.ModelId);
            if (!groups.has(key)) {
                groups.set(key, {
                    id: nextId(),
                    type: label || 'Inverter',
                    quantity: Number(inv.Quantity || 0),
                    arrays: nums,
                    options: inverterOptions,
                    selectedModel: match?.id ?? (inv.ModelId == null ? null : Number(inv.ModelId)),
                    selectedManufacturer: match?.manufacturer ?? inv.ManufacturerName ?? null,
                    moduleText: null,
                    isRemovable: false,
                });
            } else {
                const existing = groups.get(key)!;
                existing.arrays = [...new Set([...existing.arrays, ...nums])].sort((a, b) => a - b);
            }
        });
    });
    const inverterRows = [...groups.values()];
    if (inverterRows.length === 0) {
        inverterRows.push({
            id: nextId(),
            type: 'Inverter',
            quantity: 1,
            arrays: [],
            options: inverterOptions,
            selectedModel: null,
            selectedManufacturer: null,
            moduleText: null,
            isRemovable: false,
        });
    }
    inverterRows.forEach((row, index) => {
        row.type = index === 0 ? 'Inverter' : `Inverter${index + 1}`;
        row.isRemovable = index > 0;
        row.options = inverterOptions;
    });
    rows.push(...inverterRows);

    const firstInv = designArrays.find((array) => Array.isArray(array.Inverters) && array.Inverters.length)?.Inverters?.[0];
    const opt = resolveOption(optimizerOptions, firstInv?.OptimizerModel || first?.OptimizerModel, firstInv?.OptimizerModelId || first?.OptimizerModelId);
    const optId = opt?.id ?? (firstInv?.OptimizerModelId == null ? null : Number(firstInv.OptimizerModelId));
    rows.push({
        id: nextId(),
        type: 'Optimizer',
        quantity: optId ? moduleQty : Number(firstInv?.OptimizerQuantity || 0),
        arrays: [],
        options: optimizerOptions,
        selectedModel: optId,
        selectedManufacturer: opt?.manufacturer ?? firstInv?.OptimizerManufacturerName ?? null,
        moduleText: null,
        isRemovable: false,
    });

    const rack = resolveOption(rackingOptions, first.RackingModel, first.RackingModelId);
    rows.push({
        id: nextId(),
        type: 'Racking System',
        quantity: Number(first.RackingQuantity || 0),
        arrays: [],
        options: rackingOptions,
        selectedModel: rack?.id ?? (first.RackingModelId == null ? null : Number(first.RackingModelId)),
        selectedManufacturer: rack?.manufacturer ?? first.RackingManufacturerName ?? null,
        moduleText: null,
        isRemovable: false,
    });
    const rackRow = rows[rows.length - 1];
    if (rackRow.selectedModel && !rackRow.quantity) rackRow.quantity = 1;

    const savedParts = Array.isArray(first.RackingPartsWithQuantity) ? first.RackingPartsWithQuantity : [];
    const railPart = savedParts.find((part: any) => String(part.RackingType).toLowerCase() === 'rails');
    const legacyRailId = Array.isArray(first.RackingParts) && first.RackingParts.length ? Number(first.RackingParts[0]) : null;
    const railId = railPart?.RackingPartsId ?? legacyRailId;
    const railOptions = fastenerMap.Rail || fastenerMap.Rails || [];
    rows.push({
        id: nextId(),
        type: 'Rail',
        quantity: Number(railPart?.RackingQty || 0),
        arrays: [],
        options: railOptions,
        selectedModel: railId == null ? null : Number(railId),
        selectedManufacturer: railOptions.find((o) => Number(o.id) === Number(railId))?.manufacturer ?? null,
        moduleText: null,
        isRemovable: false,
    });

    FASTENER_TYPES.forEach((type) => {
        const entry = savedParts.find((part: any) => String(part.RackingType).toLowerCase() === type.toLowerCase());
        const options = fastenerMap[type] || [];
        rows.push({
            id: nextId(),
            type,
            quantity: Number(entry?.RackingQty || 0),
            arrays: [],
            options,
            selectedModel: entry?.RackingPartsId == null ? null : Number(entry.RackingPartsId),
            selectedManufacturer: options.find((o) => Number(o.id) === Number(entry?.RackingPartsId))?.manufacturer ?? null,
            moduleText: null,
            isRemovable: false,
        });
    });

    const withOptions = rows.map((row) => ({
        ...row,
        options: row.type === 'Module' ? [] : optionsForType(row.type, inverterOptions, optimizerOptions, rackingOptions, fastenerMap),
    }));
    const rackingModelId = withOptions.find((row) => row.type === 'Racking System')?.selectedModel ?? null;
    applyRackingParts(withOptions, fastenerMap, rackingModelId, designArrays);
    return withOptions;
}

function applyRowsToArrays(designArrays: Record<string, any>[], rows: EqRow[], master: MasterData): Record<string, any>[] {
    const inverterRows = rows.filter((row) => row.type.toLowerCase().includes('inverter'));
    const rackRow = rows.find((row) => row.type.toLowerCase().includes('racking system'));
    const railRow = rows.find((row) => row.type.toLowerCase() === 'rail');
    const fastRows = rows.filter((row) => FASTENER_TYPES.includes(row.type));
    const optRow = rows.find((row) => row.type.toLowerCase().includes('optimizer'));
    const modRow = rows.find((row) => row.type.toLowerCase().includes('module'));
    const primaryByRow = new Map<string, number>();
    const arraysByNumber = new Map<number, EqRow[]>();
    inverterRows.forEach((row) => {
        if (!row.arrays.length) return;
        const sorted = [...row.arrays].sort((a, b) => a - b);
        primaryByRow.set(row.id, sorted[0]);
        sorted.forEach((num) => {
            const list = arraysByNumber.get(num) || [];
            list.push(row);
            arraysByNumber.set(num, list);
        });
    });

    return designArrays.map((array, index) => {
        const next = { ...array };
        const canon = canonicalArrayNum(array, index);
        const isPrimary = index === 0;
        const uiInvs = arraysByNumber.get(canon) || [];
        const existing = Array.isArray(array.Inverters) ? array.Inverters : [];
        if (uiInvs.length) {
            next.Inverters = uiInvs.map((row) => {
                const opt = row.options.find((o) => Number(o.id) === Number(row.selectedModel));
                const orig = existing.find((inv: any) => Number(inv.ModelId) === Number(row.selectedModel)) || existing[0] || {};
                const isLeader = primaryByRow.get(row.id) === canon;
                return {
                    ...orig,
                    ModelId: row.selectedModel,
                    Model: opt?.model ?? orig.Model,
                    ManufacturerName: row.selectedManufacturer ?? orig.ManufacturerName,
                    Quantity: isLeader ? Number(row.quantity) || 0 : 0,
                    ArrayNumber: canon,
                    InverterLabel: row.type,
                };
            });
        } else if (existing.length) {
            next.Inverters = existing.map((inv: any) => ({ ...inv, Quantity: 0, OptimizerQuantity: 0, InverterLabel: '' }));
        }

        const rackOpt = rackRow?.options.find((o) => Number(o.id) === Number(rackRow.selectedModel));
        next.RackingModelId = rackRow?.selectedModel ?? null;
        next.RackingModel = rackOpt?.model ?? null;
        next.RackingManufacturerName = rackRow?.selectedManufacturer ?? null;
        if (isPrimary) {
            next.RackingQuantity = Number(rackRow?.quantity || 0);
            const parts: Array<{ id: number; qty: number; type: string }> = [];
            if (railRow?.selectedModel) parts.push({ id: Number(railRow.selectedModel), qty: railRow.quantity, type: 'Rails' });
            fastRows.forEach((row) => {
                if (row.selectedModel) parts.push({ id: Number(row.selectedModel), qty: row.quantity, type: row.type });
            });
            next.RackingPartsWithQuantity = parts.map((part) => ({ RackingPartsId: part.id, RackingType: part.type, RackingQty: part.qty }));
            next.RackingParts = parts.map((part) => part.id);
            next.RackingType = [...new Set(parts.map((part) => (part.type === 'Rails' ? 'Rails' : 'Fasteners')))];
            const rackMaster = (master?.RackingModels || []).find((item: any) => Number(item.Racking_Model_Id) === Number(rackRow?.selectedModel));
            if (rackMaster?.Finance_Partner_Racking_Model_Id) {
                next.FinancePartnerRackingModelId = rackMaster.Finance_Partner_Racking_Model_Id;
            }
        } else {
            next.RackingQuantity = 0;
            next.RackingParts = [];
            next.RackingType = [];
            next.RackingPartsWithQuantity = [];
        }

        const opt = optRow?.options.find((o) => Number(o.id) === Number(optRow.selectedModel));
        if (optRow?.selectedModel) {
            next.OptimizerModelId = optRow.selectedModel;
            next.OptimizerModel = opt?.model ?? null;
            next.OptimizerManufacturerName = optRow.selectedManufacturer;
            next.OptimizerQuantity = isPrimary ? Number(modRow?.quantity) || 0 : 0;
            next.Inverters = (next.Inverters || []).map((inv: any) => ({
                ...inv,
                OptimizerModelId: next.OptimizerModelId,
                OptimizerModel: next.OptimizerModel,
                OptimizerManufacturerName: next.OptimizerManufacturerName,
                OptimizerQuantity: next.OptimizerQuantity,
            }));
        }
        return next;
    });
}

/** Model string the domestic-content workbook expects. Falls back to the full catalog when the row's dropdown list was rebuilt for another racking system. */
function excelPartName(row: EqRow | undefined, master: MasterData): string {
    if (!row) return '';
    if (!row.selectedModel) return row.moduleText || '';
    const fromOptions = row.options.find((option) => Number(option.id) === Number(row.selectedModel))?.model;
    if (fromOptions) return fromOptions;
    if (row.moduleText) return row.moduleText;
    const id = Number(row.selectedModel);
    const catalogs: Array<{ items?: any[]; idKey: string; altIdKey?: string; nameKey: string }> = [
        { items: master?.RackingParts, idKey: 'Racking_Parts_Id', altIdKey: 'Racking_Part_Id', nameKey: 'Racking_Part_Name' },
        { items: master?.RackingModels, idKey: 'Racking_Model_Id', nameKey: 'Racking_Model_Name' },
        { items: master?.InverterModels, idKey: 'Inverter_Model_Id', nameKey: 'Inverter_Model_Name' },
        { items: master?.OptimizerModels, idKey: 'Optimizer_Model_Id', nameKey: 'Optimizer_Model_Name' },
    ];
    for (const catalog of catalogs) {
        const hit = (catalog.items || []).find((item) => (
            Number(item[catalog.idKey]) === id || (catalog.altIdKey != null && Number(item[catalog.altIdKey]) === id)
        ));
        if (hit?.[catalog.nameKey]) return String(hit[catalog.nameKey]);
    }
    return '';
}

/** Same part map as V1 `calculateDomesticPercentage`. */
function collectDomesticParts(rows: EqRow[], master: MasterData, systemDesign: RetrieveSystemDesignResponse | null): Record<string, string> {
    const parts: Record<string, string> = {};
    const moduleRow = rows.find((row) => row.type === 'Module');
    const moduleName = moduleRow?.selectedManufacturer && moduleRow.moduleText
        ? `${moduleRow.selectedManufacturer} - ${moduleRow.moduleText}`
        : (moduleRow?.moduleText || '');
    parts.Module = moduleName === '-' ? '' : moduleName;
    const inverters = rows.filter((row) => row.type.toLowerCase().includes('inverter'));
    for (let i = 0; i < 4; i++) parts[i === 0 ? 'Inverter' : `Inverter${i + 1}`] = excelPartName(inverters[i], master);
    parts.Optimizer = excelPartName(rows.find((row) => row.type === 'Optimizer'), master).replace(/ - DOMESTIC$/i, '');
    parts.Rail = excelPartName(rows.find((row) => row.type === 'Rail'), master);
    FASTENER_TYPES.forEach((type) => { parts[type] = excelPartName(rows.find((row) => row.type === type), master); });
    const rackRow = rows.find((row) => row.type === 'Racking System');
    const rackMaster = (master?.RackingModels || []).find((item: any) => Number(item.Racking_Model_Id) === Number(rackRow?.selectedModel));
    const solarPartner = String(systemDesign?.OpportunityProductsFinanceInfo?.find((item) => item?.Product_Type_Name === 'Solar')?.Finance_Partner || '');
    parts['Racking System'] = solarPartner === 'Good Leap' && rackMaster?.Finance_Partner_Racking_Model_Id
        ? String(rackMaster.Finance_Partner_Racking_Model_Id)
        : (rackMaster?.Racking_Model_Name || excelPartName(rackRow, master));
    return parts;
}

function buildSavePayload(systemDesign: RetrieveSystemDesignResponse, designArrays: Record<string, any>[], isManual: boolean) {
    const activeSolar = (systemDesign.OpportunityProductsFinanceInfo || []).find(
        (item) => item?.ActiveFlag && item?.Product_Type_Name === 'Solar',
    );
    return {
        Opportunity_Id: systemDesign.Opportunity_Id,
        OpportunityInterest: systemDesign.OpportunityInterest,
        SalesForce_Id: systemDesign.SalesForce_Id,
        SolarProductId: systemDesign.SolarProductId,
        SolarUtility: systemDesign.SolarUtility,
        BatteryInformation: systemDesign.BatteryInformation,
        BatteryProductId: systemDesign.BatteryProductId,
        Finance_Partner_Sync_CO_LeadID: (systemDesign as any).Finance_Partner_Sync_CO_LeadID,
        BatteryOnly: (systemDesign as any).BatteryOnly,
        BatteryOnlyProductId: systemDesign.BatteryOnlyProductId,
        RoofOnly: (systemDesign as any).RoofOnly,
        RoofProductId: systemDesign.RoofProductId,
        Roof: systemDesign.Roof,
        RoofOnlyProductId: systemDesign.RoofOnlyProductId,
        Multiplier: systemDesign.Multiplier,
        SystemSize: systemDesign.SystemSize,
        SystemSizeDouble: systemDesign.SystemSizeDouble,
        Production: systemDesign.Production,
        ProductionDouble: systemDesign.ProductionDouble,
        IsDomestic: systemDesign.IsDomestic ?? false,
        FinanceMethod: activeSolar?.Finance_Method_Name || systemDesign.FinanceMethod || '',
        designJson: JSON.stringify(designArrays),
        isManual,
    };
}

export function EquipmentOverridePanel({
    designArrays,
    onCloseManual,
}: {
    designArrays: Record<string, any>[];
    onCloseManual: () => void;
}) {
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const equipmentMasterData = useOneButtonStore((s) => s.equipmentMasterData);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const designId = useViewerStore((s) => s.designID);
    const idRef = useRef(1);
    const [domesticOnly, setDomesticOnly] = useState(false);
    const [override, setOverride] = useState(() => designArrays.some((array) => Array.isArray(array.Inverters) && array.Inverters.length > 1));
    const [rows, setRows] = useState<EqRow[]>(() => buildRows(designArrays, equipmentMasterData, idRef));
    const [domesticPercent, setDomesticPercent] = useState(0);
    const [saving, setSaving] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [noticeApi, noticeContext] = notification.useNotification({ maxCount: 3 });
    const showArrayOverride = enableArrayOverride(systemDesign);
    const financePartnerId = solarFinancePartnerId(systemDesign);
    const domesticReq = useRef(0);

    const refreshDomestic = (currentRows: EqRow[]) => {
        if (!financePartnerId) return;
        const requestId = ++domesticReq.current;
        const parts = collectDomesticParts(currentRows, equipmentMasterData, systemDesign);
        fetchV1DomesticPercent(parts, financePartnerId).then((percent) => {
            if (domesticReq.current === requestId) setDomesticPercent(percent);
        }).catch(() => undefined);
    };

    const arrayNumbers = useMemo(() => {
        const nums = designArrays.map((array, index) => canonicalArrayNum(array, index)).filter((n) => n > 0);
        return nums.length ? [...new Set(nums)].sort((a, b) => a - b) : [1];
    }, [designArrays]);

    useEffect(() => {
        const master = filterMaster(equipmentMasterData, domesticOnly);
        setRows((current) => {
            const rebuilt = buildRows(designArrays, master, idRef);
            return current.map((row) => {
            const match = rebuilt.find((item) => item.type === row.type);
            if (!match) return row;
            // A racking-system change selects parts that are not on the saved design.
            // Replacing those options with the original system makes the Excel payload drop the new part names.
            const selectionKept = row.selectedModel != null
                && !match.options.some((option) => Number(option.id) === Number(row.selectedModel));
            if (selectionKept) return row;
            const partRow = row.type === 'Rail' || row.type === 'Racking System' || FASTENER_TYPES.includes(row.type);
            if (partRow && (!row.selectedModel || !row.quantity) && (match.selectedModel || match.quantity)) {
                return { ...match, id: row.id };
            }
            const inverter = row.type.toLowerCase().includes('inverter');
            if (inverter && row.arrays.length === 0 && match.arrays.length > 0) {
                return { ...row, options: match.options, arrays: match.arrays };
            }
            return { ...row, options: match.options };
            });
        });
    }, [domesticOnly, equipmentMasterData, designArrays]);

    useEffect(() => {
        const timer = window.setTimeout(() => refreshDomestic(rows), 300);
        return () => window.clearTimeout(timer);
    }, [rows, financePartnerId, equipmentMasterData, systemDesign]);

    const updateRow = (id: string, patch: Partial<EqRow>) => {
        setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    };

    const onModelChange = (row: EqRow, value: number | null) => {
        const selected = row.options.find((o) => Number(o.id) === Number(value));
        const moduleQty = rows.find((item) => item.type === 'Module')?.quantity || 0;
        const quantity = row.type.toLowerCase().includes('optimizer')
            ? (value ? moduleQty : 0)
            : (value ? (row.quantity || 1) : 0);
        const next = rows.map((item) => {
            if (item.id !== row.id) return item;
            return {
                ...item,
                selectedModel: value,
                selectedManufacturer: selected?.model?.includes('Non-Domestic') ? null : (selected?.manufacturer ?? null),
                moduleText: selected?.model ?? null,
                quantity,
            };
        });
        const resolved = row.type.toLowerCase().includes('racking system')
            ? rebindPartsForRackingSystem(next, buildFastenerMap(filterMaster(equipmentMasterData, domesticOnly)), value, designArrays)
            : next;
        setRows(resolved);
        refreshDomestic(resolved);
    };

    const addInverter = () => {
        setRows((current) => {
            const inverters = current.filter((row) => row.type.toLowerCase().includes('inverter'));
            if (inverters.length >= MAX_INVERTERS) return current;
            const proto = inverters[0];
            const copy: EqRow = {
                ...proto,
                id: `local-${idRef.current++}`,
                type: `Inverter${inverters.length + 1}`,
                arrays: [],
                quantity: 1,
                isRemovable: true,
                selectedModel: null,
                selectedManufacturer: null,
            };
            const lastIndex = current.findIndex((row) => row.id === inverters[inverters.length - 1].id);
            const next = [...current];
            next.splice(lastIndex + 1, 0, copy);
            return next;
        });
    };

    const removeInverter = (id: string) => {
        setRows((current) => {
            const next = current.filter((row) => row.id !== id);
            let index = 0;
            return next.map((row) => {
                if (!row.type.toLowerCase().includes('inverter')) return row;
                index += 1;
                return { ...row, type: index === 1 ? 'Inverter' : `Inverter${index}`, isRemovable: index > 1 };
            });
        });
    };

    const toggleArray = (row: EqRow, arrayNum: number) => {
        const taken = rows.some((item) => item.id !== row.id && item.type.toLowerCase().includes('inverter') && item.arrays.includes(arrayNum));
        if (taken && !override && !row.arrays.includes(arrayNum)) return;
        const arrays = row.arrays.includes(arrayNum)
            ? row.arrays.filter((n) => n !== arrayNum)
            : [...row.arrays, arrayNum].sort((a, b) => a - b);
        updateRow(row.id, { arrays });
    };

    const calculateRacking = async () => {
        if (!designId) {
            noticeApi.warning({ message: 'Calculate Racking Quantities', description: 'A finalized design is required before racking quantities can be calculated.' });
            return;
        }
        setCalculating(true);
        try {
            const result = await packingSlipService.calculateRackingQuantities(designId);
            const qtyByType: Record<string, number | undefined> = {
                Rail: result.rail,
                Splice: result.splice,
                'Mid-Clamp': result.midClamp,
                'End-Clamp': result.endClamp,
                'MLPE Bracket': result.mlpeBracket,
            };
            setRows((current) => current.map((row) => (
                qtyByType[row.type] == null ? row : { ...row, quantity: Number(qtyByType[row.type]) || 0 }
            )));
        } finally {
            setCalculating(false);
        }
    };

    const save = async () => {
        if (!systemDesign) return;
        const messages = validateManualRows(rows, arrayNumbers);
        if (messages.length) {
            Modal.warning({
                title: '',
                content: (
                    <div>
                        {messages.map((message) => (
                            <p key={message} style={{ margin: '0 0 12px' }}>{message}</p>
                        ))}
                    </div>
                ),
                okText: 'OK',
                width: 600,
                closable: false,
                zIndex: 14000,
            });
            return;
        }
        setSaving(true);
        try {
            const nextArrays = applyRowsToArrays(designArrays, rows, equipmentMasterData);
            const payload = buildSavePayload(systemDesign, nextArrays, true);
            await designServices.SaveSystemDesign(payload);
            setSystemDesign({
                ...systemDesign,
                DesignArray: nextArrays,
                designJson: JSON.stringify(nextArrays),
                isManual: true,
            });
            noticeApi.success({ message: 'Equipment saved' });
        } catch (error) {
            noticeApi.error({ message: 'Could not save equipment', description: error instanceof Error ? error.message : undefined });
        } finally {
            setSaving(false);
        }
    };

    const domesticMet = domesticPercent >= 45;
    const panelRef = useRef<HTMLElement>(null);

    useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;
        applySharedColumnWidths(panel, EQUIPMENT_WIDTH_COLUMNS, '--eq-col-', '--eq-w-');
        applySharedColumnWidths(panel, PRODUCTION_WIDTH_COLUMNS, '--prod-col-', '--prod-w-');
    }, []);

    return (
        <section ref={panelRef} className="obd-eq-override" aria-label="Equipment Details">
            {noticeContext}
            <div className="obd-eq-override__toolbar">
                <Switch
                    checked={domesticOnly}
                    checkedChildren="Domestic"
                    unCheckedChildren="All"
                    onChange={setDomesticOnly}
                />
                <div className="obd-eq-override__toolbar-end">
                    <div className="obd-eq-override__domestic">
                        <span>Domestic Content:</span>
                        <span className={`obd-eq-override__domestic-value ${domesticMet ? 'is-met' : 'is-short'}`}>
                            {domesticPercent}%
                        </span>
                    </div>
                    <Switch
                        checked
                        checkedChildren="Manual"
                        unCheckedChildren="Automatic"
                        onChange={(checked) => { if (!checked) onCloseManual(); }}
                    />
                </div>
            </div>
            <div className="obd-eq-override__table-wrap">
                <table className="obd-eq-override__table">
                    <thead>
                        <tr>
                            <th>Equipment Type</th>
                            <th>Equipment Model</th>
                            <th>Manufacturer</th>
                            <th>Quantity</th>
                            <th>
                                Arrays
                                {showArrayOverride && (
                                    <Switch
                                        size="small"
                                        checked={override}
                                        checkedChildren="Override"
                                        unCheckedChildren="Override"
                                        onChange={setOverride}
                                    />
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const isInverter = row.type.toLowerCase().includes('inverter');
                            const qtyReadonly = row.type === 'Module' || row.type === 'Optimizer';
                            const moduleLabel = row.selectedManufacturer && row.moduleText
                                ? `${row.selectedManufacturer} - ${row.moduleText}`
                                : (row.moduleText || '—');
                            return (
                                <tr key={row.id}>
                                    <td>
                                        <span>{row.type}</span>
                                        {isInverter && !row.isRemovable && (
                                            <button type="button" className="obd-eq-override__icon" onClick={addInverter} aria-label="Add inverter" disabled={rows.filter((item) => item.type.toLowerCase().includes('inverter')).length >= MAX_INVERTERS}>
                                                <PlusCircle size={18} color="#22c55e" weight="fill" />
                                            </button>
                                        )}
                                        {row.isRemovable && (
                                            <button type="button" className="obd-eq-override__icon" onClick={() => removeInverter(row.id)} aria-label="Remove inverter">
                                                <MinusCircle size={18} color="#ef4444" weight="fill" />
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        {row.type === 'Module' ? (
                                            <span>{moduleLabel}</span>
                                        ) : (
                                            <Select
                                                value={row.selectedModel ?? undefined}
                                                allowClear
                                                placeholder="Select"
                                                style={{ width: '100%' }}
                                                options={row.options.filter((opt) => opt.id != null).map((opt) => ({ value: Number(opt.id), label: opt.model || '' }))}
                                                onChange={(value) => onModelChange(row, value == null ? null : Number(value))}
                                            />
                                        )}
                                    </td>
                                    <td>{row.selectedManufacturer || ''}</td>
                                    <td>
                                        {qtyReadonly ? (
                                            <span>{row.quantity || 0}</span>
                                        ) : (
                                            <InputNumber
                                                min={0}
                                                value={row.quantity}
                                                onChange={(value) => updateRow(row.id, { quantity: Number(value) || 0 })}
                                                className="obd-eq-override__qty"
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {isInverter && arrayNumbers.map((arrayNum) => {
                                            const taken = rows.some((item) => item.id !== row.id && item.type.toLowerCase().includes('inverter') && item.arrays.includes(arrayNum));
                                            return (
                                                <Checkbox
                                                    key={arrayNum}
                                                    checked={row.arrays.includes(arrayNum)}
                                                    disabled={taken && !override && !row.arrays.includes(arrayNum)}
                                                    onChange={() => toggleArray(row, arrayNum)}
                                                >
                                                    {arrayNum}
                                                </Checkbox>
                                            );
                                        })}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="obd-eq-override__actions">
                <Button type="primary" loading={calculating} onClick={calculateRacking}>Calculate Racking Quantities</Button>
                <Button type="primary" loading={saving} onClick={save}>Save</Button>
            </div>
            <table className="obd-eq-override__production">
                <thead>
                    <tr>
                        <th>Array</th>
                        <th>Tilt</th>
                        <th>Azimuth</th>
                        <th>Solar Availability</th>
                        <th>Building type</th>
                        <th>Module Quantity</th>
                    </tr>
                </thead>
                <tbody>
                    {designArrays.map((array, index) => (
                        <tr key={index}>
                            <td>Array {index + 1}</td>
                            <td>{array.Tilt ?? ''}</td>
                            <td>{array.Azimuth ?? ''}</td>
                            <td>{array.SolarAvailability ?? ''}</td>
                            <td>{array.BuildingType && array.BuildingType !== 'Common' ? array.BuildingType : 'Roof Mount'}</td>
                            <td>{array.Quantity ?? ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}
